// Fictional Employee Dataset for Dynami Demo

export interface Employee {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  manager: string;
  startDate: string;
  salary: number;
  riskScore: number;
  riskLevel: 'High' | 'Medium' | 'Low';
  engagementTrend: 'declining' | 'stable' | 'improving';
  behavioralFlags: string[];
  lastCheckIn: string;
  recentActivity: {
    meetingsAttended: number;
    messagesResponse: number; // average hours to respond
    tasksCompleted: number;
    overtimeHours: number;
    ptoUsed: number;
  };
  riskExplanation: string;
  notes: string;
  avatar?: string;
}

export interface Department {
  name: string;
  headCount: number;
  avgRiskScore: number;
  highRiskCount: number;
  mediumRiskCount: number;
  lowRiskCount: number;
}

const firstNames = [
  'Alex', 'Jordan', 'Taylor', 'Morgan', 'Casey', 'Riley', 'Quinn', 'Avery',
  'Peyton', 'Cameron', 'Drew', 'Reese', 'Dakota', 'Skyler', 'Jamie', 'Kendall',
  'Parker', 'Hayden', 'Emerson', 'Logan', 'Blake', 'Charlie', 'Finley', 'Phoenix',
  'Rowan', 'Sage', 'River', 'Eden', 'Marlowe', 'Harley', 'Lennox', 'Ellis',
  'Shawn', 'Jesse', 'Angel', 'Frankie', 'Jody', 'Kerry', 'Robin', 'Leslie',
  'Sam', 'Pat', 'Chris', 'Lee', 'Kim', 'Terry', 'Dana', 'Corey'
];

const lastNames = [
  'Chen', 'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson',
  'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson',
  'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker',
  'Young', 'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill',
  'Flores', 'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell'
];

const departments = [
  'Engineering', 'Product', 'Design', 'Marketing', 'Sales', 
  'Customer Success', 'HR', 'Finance', 'Operations', 'Legal'
];

const roles: Record<string, string[]> = {
  'Engineering': ['Software Engineer', 'Senior Software Engineer', 'Staff Engineer', 'Engineering Manager', 'DevOps Engineer', 'QA Engineer'],
  'Product': ['Product Manager', 'Senior Product Manager', 'Product Analyst', 'Product Owner'],
  'Design': ['Product Designer', 'Senior Designer', 'UX Researcher', 'Design Lead'],
  'Marketing': ['Marketing Manager', 'Content Strategist', 'Growth Manager', 'Brand Manager', 'Marketing Coordinator'],
  'Sales': ['Account Executive', 'Sales Manager', 'SDR', 'Enterprise Account Manager', 'Sales Director'],
  'Customer Success': ['Customer Success Manager', 'Support Specialist', 'Implementation Manager', 'Success Director'],
  'HR': ['HR Business Partner', 'Recruiter', 'HR Coordinator', 'People Operations Manager'],
  'Finance': ['Financial Analyst', 'Accountant', 'Finance Manager', 'Controller'],
  'Operations': ['Operations Manager', 'Project Manager', 'Business Analyst', 'Operations Coordinator'],
  'Legal': ['Legal Counsel', 'Paralegal', 'Compliance Manager']
};

const managers: Record<string, string> = {
  'Engineering': 'Sarah Chen',
  'Product': 'Michael Rodriguez',
  'Design': 'Emma Thompson',
  'Marketing': 'David Kim',
  'Sales': 'Jennifer Walsh',
  'Customer Success': 'Robert Martinez',
  'HR': 'Lisa Anderson',
  'Finance': 'James Wilson',
  'Operations': 'Maria Garcia',
  'Legal': 'Thomas Brown'
};

const behavioralFlagOptions = [
  'Decreased meeting attendance',
  'Slower response times',
  'Reduced collaboration',
  'Increased overtime',
  'Missed deadlines',
  'Reduced communication',
  'Skipped 1:1s',
  'Negative sentiment in messages',
  'Reduced code commits',
  'Lower task completion rate',
  'Increased PTO requests',
  'Late arrivals',
  'Shortened working hours',
  'Declined team events',
  'Reduced participation in discussions'
];

const riskExplanations = {
  high: [
    'Engagement patterns show significant decline over the past 4 weeks. Multiple behavioral indicators suggest potential burnout or job dissatisfaction. Response times have increased by 150% and meeting attendance has dropped by 40%.',
    'Analysis indicates a high probability of disengagement. The employee has shown consistent decline in participation metrics, with a notable decrease in proactive communication and collaboration with team members.',
    'Risk assessment elevated due to combination of factors: extended overtime hours, decreased quality of deliverables, and withdrawal from team activities. Pattern matches historical turnover indicators.',
    'Multiple signals suggest urgent attention needed. The employee has missed several scheduled 1:1s, response latency has tripled, and peer feedback indicates reduced engagement in collaborative projects.'
  ],
  medium: [
    'Some engagement indicators show moderate concern. While overall performance remains stable, there are early signs of decreased enthusiasm and minor delays in task completion.',
    'Engagement levels are trending slightly downward. Recent patterns suggest the employee may be experiencing work-related stress or reduced motivation. Recommend proactive check-in.',
    'Mixed signals detected. While productivity metrics remain acceptable, soft indicators like meeting participation and spontaneous communication have decreased noticeably.',
    'Moderate risk based on subtle changes in work patterns. The employee\'s engagement trajectory suggests they may benefit from manager attention and support.'
  ],
  low: [
    'Strong engagement indicators across all measured dimensions. The employee demonstrates consistent participation, timely responses, and positive collaboration patterns.',
    'Engagement profile is healthy. Regular participation in team activities, consistent communication patterns, and stable productivity metrics indicate good workplace satisfaction.',
    'Low risk assessment based on positive engagement trends. The employee shows signs of being well-integrated with their team and motivated in their role.',
    'All engagement indicators within healthy ranges. The employee demonstrates strong connectivity with colleagues and maintains consistent performance levels.'
  ]
};

function generateEmployee(index: number): Employee {
  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  const name = `${firstName} ${lastName}`;
  const department = departments[Math.floor(Math.random() * departments.length)];
  const departmentRoles = roles[department];
  const role = departmentRoles[Math.floor(Math.random() * departmentRoles.length)];
  
  // Generate risk score with realistic distribution
  // 15% high risk, 30% medium risk, 55% low risk
  const riskRand = Math.random();
  let riskScore: number;
  let riskLevel: 'High' | 'Medium' | 'Low';
  
  if (riskRand < 0.15) {
    riskScore = Math.floor(Math.random() * 25) + 75; // 75-99
    riskLevel = 'High';
  } else if (riskRand < 0.45) {
    riskScore = Math.floor(Math.random() * 30) + 40; // 40-69
    riskLevel = 'Medium';
  } else {
    riskScore = Math.floor(Math.random() * 35) + 5; // 5-39
    riskLevel = 'Low';
  }
  
  // Generate behavioral flags based on risk level
  const numFlags = riskLevel === 'High' ? Math.floor(Math.random() * 3) + 3 :
                   riskLevel === 'Medium' ? Math.floor(Math.random() * 2) + 1 :
                   Math.random() < 0.3 ? 1 : 0;
  
  const shuffledFlags = [...behavioralFlagOptions].sort(() => Math.random() - 0.5);
  const flags = shuffledFlags.slice(0, numFlags);
  
  // Generate engagement trend
  const trendRand = Math.random();
  const engagementTrend: 'declining' | 'stable' | 'improving' = 
    riskLevel === 'High' ? (trendRand < 0.8 ? 'declining' : 'stable') :
    riskLevel === 'Medium' ? (trendRand < 0.4 ? 'declining' : trendRand < 0.7 ? 'stable' : 'improving') :
    (trendRand < 0.1 ? 'declining' : trendRand < 0.5 ? 'stable' : 'improving');
  
  // Generate salary based on role seniority
  const baseSalary = 60000;
  const seniorityMultiplier = role.includes('Senior') ? 1.5 :
                              role.includes('Manager') || role.includes('Lead') ? 1.7 :
                              role.includes('Director') ? 2.0 :
                              role.includes('Staff') ? 1.8 : 1.0;
  const salary = Math.floor(baseSalary * seniorityMultiplier * (0.9 + Math.random() * 0.3));
  
  // Generate start date (between 6 months and 5 years ago)
  const now = new Date();
  const daysAgo = Math.floor(Math.random() * 1640) + 180;
  const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
  
  // Generate last check-in (within last 30 days)
  const checkInDaysAgo = Math.floor(Math.random() * 30);
  const lastCheckIn = new Date(now.getTime() - checkInDaysAgo * 24 * 60 * 60 * 1000);
  
  // Generate recent activity based on risk level
  const recentActivity = {
    meetingsAttended: riskLevel === 'High' ? Math.floor(Math.random() * 5) + 2 :
                      riskLevel === 'Medium' ? Math.floor(Math.random() * 5) + 5 :
                      Math.floor(Math.random() * 5) + 8,
    messagesResponse: riskLevel === 'High' ? Math.floor(Math.random() * 12) + 6 :
                      riskLevel === 'Medium' ? Math.floor(Math.random() * 4) + 2 :
                      Math.floor(Math.random() * 2) + 0.5,
    tasksCompleted: riskLevel === 'High' ? Math.floor(Math.random() * 5) + 3 :
                    riskLevel === 'Medium' ? Math.floor(Math.random() * 5) + 6 :
                    Math.floor(Math.random() * 5) + 10,
    overtimeHours: riskLevel === 'High' ? Math.floor(Math.random() * 20) + 10 :
                   riskLevel === 'Medium' ? Math.floor(Math.random() * 10) + 2 :
                   Math.floor(Math.random() * 5),
    ptoUsed: riskLevel === 'High' ? Math.floor(Math.random() * 3) :
             riskLevel === 'Medium' ? Math.floor(Math.random() * 5) + 2 :
             Math.floor(Math.random() * 8) + 3
  };
  
  // Select risk explanation
  const explanations = riskExplanations[riskLevel.toLowerCase() as keyof typeof riskExplanations];
  const riskExplanation = explanations[Math.floor(Math.random() * explanations.length)];
  
  return {
    id: `emp-${String(index + 1).padStart(4, '0')}`,
    name,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@company.com`,
    role,
    department,
    manager: managers[department],
    startDate: startDate.toISOString().split('T')[0],
    salary,
    riskScore,
    riskLevel,
    engagementTrend,
    behavioralFlags: flags,
    lastCheckIn: lastCheckIn.toISOString().split('T')[0],
    recentActivity,
    riskExplanation,
    notes: '',
    avatar: `https://api.dicebear.com/7.x/personas/svg?seed=${firstName}${lastName}`
  };
}

export function generateEmployeeDataset(count: number = 80): Employee[] {
  const employees: Employee[] = [];
  const usedNames = new Set<string>();
  
  for (let i = 0; i < count; i++) {
    let employee = generateEmployee(i);
    // Ensure unique names
    while (usedNames.has(employee.name)) {
      employee = generateEmployee(i);
    }
    usedNames.add(employee.name);
    employees.push(employee);
  }
  
  return employees;
}

export function calculateDepartmentStats(employees: Employee[]): Department[] {
  const deptMap = new Map<string, Employee[]>();
  
  employees.forEach(emp => {
    if (!deptMap.has(emp.department)) {
      deptMap.set(emp.department, []);
    }
    deptMap.get(emp.department)!.push(emp);
  });
  
  return Array.from(deptMap.entries()).map(([name, emps]) => ({
    name,
    headCount: emps.length,
    avgRiskScore: Math.round(emps.reduce((sum, e) => sum + e.riskScore, 0) / emps.length),
    highRiskCount: emps.filter(e => e.riskLevel === 'High').length,
    mediumRiskCount: emps.filter(e => e.riskLevel === 'Medium').length,
    lowRiskCount: emps.filter(e => e.riskLevel === 'Low').length,
  }));
}

export function calculateOverallStats(employees: Employee[]) {
  const highRisk = employees.filter(e => e.riskLevel === 'High');
  const mediumRisk = employees.filter(e => e.riskLevel === 'Medium');
  const lowRisk = employees.filter(e => e.riskLevel === 'Low');
  
  const avgSalary = employees.reduce((sum, e) => sum + e.salary, 0) / employees.length;
  const disengagementCost = highRisk.reduce((sum, e) => sum + e.salary * 0.34, 0) + 
                            mediumRisk.reduce((sum, e) => sum + e.salary * 0.15, 0);
  
  const declining = employees.filter(e => e.engagementTrend === 'declining').length;
  const stable = employees.filter(e => e.engagementTrend === 'stable').length;
  const improving = employees.filter(e => e.engagementTrend === 'improving').length;
  
  // Aggregate behavioral flags
  const flagCounts = new Map<string, number>();
  employees.forEach(emp => {
    emp.behavioralFlags.forEach(flag => {
      flagCounts.set(flag, (flagCounts.get(flag) || 0) + 1);
    });
  });
  
  const topFlags = Array.from(flagCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([flag, count]) => ({ flag, count }));
  
  return {
    totalEmployees: employees.length,
    highRiskCount: highRisk.length,
    mediumRiskCount: mediumRisk.length,
    lowRiskCount: lowRisk.length,
    percentDisengaged: Math.round((highRisk.length / employees.length) * 100),
    avgRiskScore: Math.round(employees.reduce((sum, e) => sum + e.riskScore, 0) / employees.length),
    estimatedCost: Math.round(disengagementCost),
    trendingDown: declining,
    trendingStable: stable,
    trendingUp: improving,
    topBehavioralFlags: topFlags,
    avgSalary: Math.round(avgSalary),
  };
}

// Pre-generate a default dataset
export const defaultEmployees = generateEmployeeDataset(80);
export const defaultDepartments = calculateDepartmentStats(defaultEmployees);
export const defaultStats = calculateOverallStats(defaultEmployees);

// Generate historical data for trends
export function generateTrendData(weeks: number = 12) {
  const data = [];
  const baseHigh = 15;
  const baseMedium = 30;
  const baseLow = 55;
  
  for (let i = weeks - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i * 7);
    
    const variance = Math.sin(i * 0.5) * 3;
    
    data.push({
      week: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullDate: date.toISOString().split('T')[0],
      highRisk: Math.round(baseHigh + variance + Math.random() * 2),
      mediumRisk: Math.round(baseMedium - variance * 0.5 + Math.random() * 3),
      lowRisk: Math.round(baseLow + variance * 0.3 + Math.random() * 2),
      avgScore: Math.round(35 + variance + Math.random() * 5),
    });
  }
  
  return data;
}

export const defaultTrendData = generateTrendData(12);


